import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Calendar, Clock, ArrowRight, BookOpen, Star, X } from 'lucide-react';
import { useAppointment } from '../context/AppointmentContext';

const CATEGORY_ICONS = {
  'Astrology': '✨',
  'Yoga & Wellness': '🌿',
};

const TAG_COLORS = {
  Jyotish: 'blog-tag-amber',
  Constellations: 'blog-tag-orange',
  Mindfulness: 'blog-tag-emerald',
};

/* ── Article Reader Modal ─────────────────────────────────────────────── */
const ArticleReader = ({ post, onClose }) => {
  if (!post) return null;
  const tagClass = TAG_COLORS[post.tag] || 'blog-tag-default';

  return (
    <div className="blog-reader-overlay" onClick={onClose}>
      <div className="blog-reader-modal" onClick={e => e.stopPropagation()}>
        {/* Header image */}
        <div className="blog-reader-hero">
          {post.coverImage ? (
            <img src={post.coverImage} alt={post.title} className="blog-reader-hero-img" />
          ) : (
            <div className="blog-reader-hero-gradient" style={{ background: post.coverGradient }} />
          )}
          <div className="blog-reader-hero-overlay" />
          <button className="blog-reader-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {/* Article body */}
        <div className="blog-reader-body">
          <div className="blog-reader-meta-bar">
            <span className={`blog-reader-tag ${tagClass}`}>{post.tag}</span>
            <span className="blog-reader-meta-item"><Calendar size={13} />{post.date}</span>
            <span className="blog-reader-meta-item"><Clock size={13} />{post.readTime}</span>
          </div>

          <h1 className="blog-reader-title">{post.title}</h1>

          <div className="blog-reader-author-row">
            <div
              className="blog-author-avatar"
              style={{ background: post.authorColor || '#6b5ce7' }}
            >
              {post.authorInitials || 'SR'}
            </div>
            <div>
              <div className="blog-reader-author-name">{post.authorName || 'Samudra Rimal'}</div>
              <div className="blog-reader-author-sub">{post.category}</div>
            </div>
          </div>

          <div className="blog-reader-divider" />

          <div
            className="blog-reader-content"
            dangerouslySetInnerHTML={{ __html: post.content || '<p>Full article content coming soon…</p>' }}
          />
        </div>
      </div>
    </div>
  );
};

/* ── Blog Card ────────────────────────────────────────────────────────── */
const BlogCard = ({ post, onReadMore }) => {
  const tagClass = TAG_COLORS[post.tag] || 'blog-tag-default';
  const categoryIcon = CATEGORY_ICONS[post.category] || '📝';

  return (
    <article className="blog-card">
      {/* Cover */}
      <div className="blog-card-cover" aria-hidden="true">
        {post.coverImage ? (
          <img
            src={post.coverImage}
            alt={post.title}
            className="blog-card-cover-img"
            loading="lazy"
          />
        ) : (
          <div
            className="blog-card-cover-gradient"
            style={{ background: post.coverGradient || 'linear-gradient(135deg, #6b5ce7, #a8f0e0)' }}
          />
        )}
        <div className="blog-cover-overlay" />
        <span className="blog-cover-icon">{categoryIcon}</span>
        <span className={`blog-cover-tag ${tagClass}`}>{post.tag}</span>
      </div>

      {/* Body */}
      <div className="blog-card-body">
        <div className="blog-card-meta">
          <span className="blog-meta-item"><Calendar size={13} />{post.date}</span>
          <span className="blog-meta-item"><Clock size={13} />{post.readTime}</span>
        </div>
        <h3 className="blog-card-title">{post.title}</h3>
        <p className="blog-card-excerpt">{post.excerpt}</p>
        <div className="blog-card-footer">
          <div className="blog-author">
            <div
              className="blog-author-avatar"
              style={{ background: post.authorColor || '#6b5ce7' }}
              aria-label={post.authorName}
            >
              {post.authorInitials || 'SR'}
            </div>
            <span className="blog-author-name">{post.authorName || 'Samudra Rimal'}</span>
          </div>
          <button
            onClick={() => onReadMore(post)}
            className="blog-read-more"
            id={`blog-read-${post.id}`}
          >
            Read More
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </article>
  );
};

/* ── Blog Page ────────────────────────────────────────────────────────── */
const Blog = ({ isPreview = false }) => {
  const { blogs } = useAppointment();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeArticle, setActiveArticle] = useState(null);

  const categories = useMemo(() => {
    const set = new Set(['Astrology', 'Yoga & Wellness']);
    (blogs || []).forEach((b) => {
      if (b.category && b.category.trim()) {
        set.add(b.category.trim());
      }
    });
    return ['All', ...Array.from(set)];
  }, [blogs]);

  const filteredPosts = blogs.filter((post) => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch =
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const displayedPosts = isPreview ? filteredPosts.slice(0, 3) : filteredPosts;

  return (
    <div className={`appt-page ${isPreview ? 'appt-page-preview' : ''}`}>

      {/* ── Hero (Hidden in Preview) ─────────────────────────────────── */}
      {!isPreview && (
        <div className="appt-hero">
          <div className="appt-hero-badge">
            <Star size={14} />
            <span>Shivoham Journal</span>
          </div>
          <h1 className="appt-hero-title">
            The Sacred <span>Chronicles</span>
          </h1>
          <p className="appt-hero-sub">
            Sharing lessons, guides, and thoughts on astrology, Vedic rituals, and spiritual wellness.
          </p>
        </div>
      )}

      <div className="appt-container">

        {/* ── Filter + Search Bar (Hidden in Preview) ───────────────────── */}
        {!isPreview && (
          <div className="blog-filter-bar appt-fade-in">
            <div className="blog-filter-chips">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`blog-filter-chip ${selectedCategory === cat ? 'active' : ''}`}
                  id={`blog-cat-${cat.replace(/\s/g, '-').toLowerCase()}`}
                >
                  <span>{CATEGORY_ICONS[cat] || (cat === 'All' ? '🌌' : '📜')}</span>
                  {cat}
                </button>
              ))}
            </div>
            <div className="blog-search-wrap">
              <Search size={15} className="blog-search-icon" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="blog-search-input"
                id="blog-search"
              />
            </div>
          </div>
        )}

        {/* ── Cards Grid ──────────────────────────────────────────────── */}
        <div className="blog-grid-section appt-fade-in">
          {displayedPosts.length > 0 ? (
            <div className="blog-grid">
              {displayedPosts.map((post) => (
                <BlogCard
                  key={post.id}
                  post={post}
                  onReadMore={setActiveArticle}
                />
              ))}
            </div>
          ) : (
            <div className="appt-card blog-empty">
              <BookOpen size={40} />
              <p>No articles found matching your filters.</p>
            </div>
          )}
        </div>

        {/* See More Blogs Button for MainPage Preview */}
        {isPreview && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '35px', marginBottom: '10px' }}>
            <Link to="/blog" className="appt-btn-primary" style={{ display: 'inline-flex', padding: '12px 28px', borderRadius: '12px', textDecoration: 'none' }}>
              See More Articles
            </Link>
          </div>
        )}

      </div>

      {/* ── Article Reader ────────────────────────────────────────────── */}
      {activeArticle && (
        <ArticleReader
          post={activeArticle}
          onClose={() => setActiveArticle(null)}
        />
      )}
    </div>
  );
};

export default Blog;
